import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { authApi, type User } from '../api/auth';

type AuthErrorResponse = {
  code?: string;
  message?: string;
};

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  login: () => void;
  logout: () => void;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: authApi.getMe,
    retry: false, // Don't retry on 401
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const login = () => {
    window.location.href = authApi.getLoginUrl();
  };

  const logout = () => {
    // Clear the auth cache before redirecting
    queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
    window.location.href = authApi.getLogoutUrl();
  };

  useEffect(() => {
    const authError = error as AxiosError<AuthErrorResponse> | null;

    if (authError?.response?.data?.code !== 'USER_NOT_FOUND') {
      return;
    }

    queryClient.removeQueries({ queryKey: AUTH_QUERY_KEY });
    window.location.href = authApi.getLogoutUrl();
  }, [error, queryClient]);

  const value: AuthContextValue = {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading,
    error: error as Error | null,
    login,
    logout,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
